'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { AlertTriangle, BadgeCheck, ArrowRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface AttentionItem {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
}

export default function DashboardNeedsAttention() {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAttentionItems() {
      const supabase = createClient();

      const [
        escalatedDisputesResult,
        overdueVerificationsResult,
      ] = await Promise.all([
        supabase
          .from('disputes')
          .select('id, job_title, customer_name, artisan_name')
          .eq('status', 'escalated')
          .limit(2),
        supabase
          .from('artisan_verifications')
          .select('id, name')
          .eq('is_overdue', true)
          .eq('status', 'under_review')
          .limit(5),
      ]);

      const escalatedDisputes = escalatedDisputesResult.data;
      const overdueVerifications = overdueVerificationsResult.data;

      const attentionItems: AttentionItem[] = [];

      escalatedDisputes?.forEach((d) => {
        attentionItems.push({
          id: `dispute-${d.id}`,
          title: `Dispute ${d.id} escalated`,
          description: `Customer ${d.customer_name} vs artisan ${d.artisan_name} · ${d.job_title}`,
          href: '/dispute-management',
          icon: AlertTriangle,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
        });
      });

      if ((overdueVerifications?.length ?? 0) > 0) {
        const names = overdueVerifications?.map((v) => v.name).join(', ') ?? '';
        attentionItems.push({
          id: 'overdue-verifications',
          title: `${overdueVerifications?.length} verification${(overdueVerifications?.length ?? 0) > 1 ? 's' : ''} overdue (>48h)`,
          description: names,
          href: '/artisan-verification-queue',
          icon: BadgeCheck,
          color: 'text-amber-700',
          bg: 'bg-amber-50',
          border: 'border-amber-200',
        });
      }

      setItems(attentionItems);
      setLoading(false);
    }

    fetchAttentionItems();
  }, []);

  if (loading) {
    return (
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-700 text-foreground">Needs Attention</h3>
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`att-skel-${i}`} className="flex items-start gap-3 px-5 py-4">
              <div className="w-8 h-8 rounded-lg bg-muted animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-muted animate-pulse rounded w-3/4" />
                <div className="h-2.5 bg-muted animate-pulse rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card-elevated rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-700 text-foreground">Needs Attention</h3>
        {items.length > 0 && (
          <span className="text-xs font-600 text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm font-600 text-foreground mb-1">All clear!</p>
          <p className="text-xs text-muted-foreground">No items require immediate attention.</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {items?.map((item) => {
            const Icon = item?.icon;
            return (
              <Link
                key={item?.id}
                href={item?.href}
                className={`flex items-start gap-3 px-5 py-4 hover:bg-muted/50 transition-colors group ${item?.bg} border-l-2 ${item?.border}`}
              >
                <div className={`mt-0.5 p-1.5 rounded-lg ${item?.bg}`}>
                  <Icon size={14} className={item?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-600 text-foreground">{item?.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {item?.description}
                  </p>
                </div>
                <ArrowRight
                  size={14}
                  className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-0.5"
                />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}